<script lang="ts">
  import { getDrawerStore } from '@skeletonlabs/skeleton'

  import LabelStudioIcon from '@components/LabelStudioIcon.svelte'
  import {
    type MobileDrawerMeta,
    type NavigationSubMenu,
    type NavigationSubMenuItem,
  } from '@utils/navigation'

  const drawerStore = getDrawerStore()

  const getManageItemPath = (manageItem: MobileDrawerMeta['manageItems'][number]) =>
    manageItem.root ? manageItem.path : `/manage${manageItem.path}`

  const getSubMenuItemPath = (subMenu: NavigationSubMenu, subMenuItem: NavigationSubMenuItem) =>
    subMenuItem.root ? subMenuItem.path : `${subMenu.path}${subMenuItem.path}`

  const closeDrawer = () => {
    drawerStore.close()
  }

  const runLogout = async (logout: MobileDrawerMeta['logout']) => {
    await logout()
    closeDrawer()
  }
</script>

{#if $drawerStore.meta}
  {@const meta = $drawerStore.meta as MobileDrawerMeta}
  <nav class="flex h-full min-h-[100dvh] flex-col gap-7 overflow-y-auto px-6 pb-10 pt-8">
    <div class="flex items-center justify-between">
      <span
        class="text-xs font-semibold uppercase tracking-[0.2em] text-surface-500 dark:text-surface-300"
      >
        Menu
      </span>
      <button
        class="variant-ghost btn-icon btn-icon-sm flex h-8 w-8 items-center justify-center leading-none [&>svg.block.h-4]:ml-0"
        type="button"
        onclick={closeDrawer}
      >
        <span class="sr-only">Close navigation</span>
        <svg viewBox="0 0 24 24" aria-hidden="true" class="block h-4 w-4">
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>

    <ul class="flex flex-col gap-4 text-sm font-semibold uppercase tracking-[0.08em]">
      <li>
        <details class="group">
          <summary
            class="flex cursor-pointer items-center justify-between text-surface-900 dark:text-surface-50"
          >
            Manage
            <span
              class="text-xs text-surface-500 transition group-open:rotate-90 dark:text-surface-300"
            >
              ▶
            </span>
          </summary>
          <ul
            class="mt-3 flex flex-col gap-2 border-l-2 border-surface-400 pl-4 text-sm font-medium normal-case tracking-normal dark:border-surface-300"
          >
            {#each meta.manageItems as manageItem (manageItem.path)}
              <li>
                <a
                  href={getManageItemPath(manageItem)}
                  target={manageItem.newTab ? '_blank' : undefined}
                  rel={manageItem.newTab ? 'noreferrer noopener' : undefined}
                  class="flex items-center gap-2 text-surface-700 transition hover:text-primary-500 dark:text-surface-200"
                  onclick={closeDrawer}
                >
                  {#if manageItem.label === 'Label Studio'}
                    <span class="inline-flex items-center gap-2">
                      {manageItem.label}
                      <LabelStudioIcon />
                    </span>
                  {:else}
                    {manageItem.label}
                  {/if}
                </a>
              </li>
            {/each}
          </ul>
        </details>
      </li>

      {#if meta.showStorageLink}
        <li>
          <a
            href="/storage-configs"
            class="text-surface-900 transition hover:text-primary-500 dark:text-surface-50"
            onclick={closeDrawer}
          >
            Storage
          </a>
        </li>
      {/if}

      {#each meta.productSubMenus as subMenu (subMenu.title)}
        {#if subMenu.items}
          <li>
            <details class="group">
              <summary
                class="flex cursor-pointer items-center justify-between text-surface-900 dark:text-surface-50"
              >
                {subMenu.title}
                <span
                  class="text-xs text-surface-500 transition group-open:rotate-90 dark:text-surface-300"
                >
                  ▶
                </span>
              </summary>
              <ul
                class="mt-3 flex flex-col gap-2 border-l-2 border-surface-400 pl-4 text-sm font-medium normal-case tracking-normal dark:border-surface-300"
              >
                {#each subMenu.items as subMenuItem (subMenuItem.path)}
                  <li>
                    <a
                      href={getSubMenuItemPath(subMenu, subMenuItem)}
                      target={subMenuItem.newTab ? '_blank' : undefined}
                      rel={subMenuItem.newTab ? 'noreferrer noopener' : undefined}
                      class="text-surface-700 transition hover:text-primary-500 dark:text-surface-200"
                      onclick={closeDrawer}
                    >
                      {subMenuItem.label}
                    </a>
                  </li>
                {/each}
              </ul>
            </details>
          </li>
        {:else}
          <li>
            <a
              href={subMenu.path}
              class="text-surface-900 transition hover:text-primary-500 dark:text-surface-50"
              onclick={closeDrawer}
            >
              {subMenu.title}
            </a>
          </li>
        {/if}
      {/each}

      <li>
        <button
          class="text-left text-primary-500 transition hover:text-primary-400"
          type="button"
          onclick={() => runLogout(meta.logout)}
        >
          Logout
        </button>
      </li>
    </ul>
  </nav>
{/if}
